import { useCallback, useEffect, useRef, useState } from "react";

export const MIN_SCALE = 1;
export const MAX_SCALE = 4;

export function usePinchPan(containerRef: React.RefObject<HTMLDivElement>) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [hintAt, setHintAt] = useState(0);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStart = useRef<{ dist: number; scale: number; midX: number; midY: number; tx: number; ty: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);

  const clamp = (s: number, x: number, y: number) => {
    const el = containerRef.current;
    if (!el) return { s, x, y };
    const w = el.clientWidth;
    const h = el.clientHeight;
    const ns = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
    const minX = w - ns * w;
    const minY = h - ns * h;
    const nx = Math.min(0, Math.max(minX, x));
    const ny = Math.min(0, Math.max(minY, y));
    return { s: ns, x: nx, y: ny };
  };

  // Track latest transform in refs so the stable `reset` callback never reads stale state.
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { txRef.current = tx; }, [tx]);
  useEffect(() => { tyRef.current = ty; }, [ty]);

  const apply = (s: number, x: number, y: number, opts?: { silent?: boolean }) => {
    const c = clamp(s, x, y);
    setScale(c.s);
    setTx(c.x);
    setTy(c.y);
    if (!opts?.silent) setHintAt(Date.now());
  };

  // Stable across renders. Bails when already at identity or while a gesture is in progress,
  // and never flashes the zoom-percent hint. Returns true when a reset actually happened.
  const reset = useCallback(() => {
    if (pointers.current.size > 0) return false;
    if (scaleRef.current === 1 && txRef.current === 0 && tyRef.current === 0) return false;
    setScale(1);
    setTx(0);
    setTy(0);
    return true;
  }, []);

  const setScaleAt = (newScale: number, anchorX: number, anchorY: number) => {
    // Keep the point under (anchorX, anchorY) stationary while scaling.
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = anchorX - rect.left;
    const py = anchorY - rect.top;
    const ratio = newScale / scale;
    const nx = px - (px - tx) * ratio;
    const ny = py - (py - ty) * ratio;
    apply(newScale, nx, ny);
  };

  const zoomIn = () => {
    const el = containerRef.current;
    if (!el) return apply(Math.min(MAX_SCALE, scale * 1.5), tx, ty);
    const rect = el.getBoundingClientRect();
    setScaleAt(Math.min(MAX_SCALE, scale * 1.5), rect.left + rect.width / 2, rect.top + rect.height / 2);
  };
  const zoomOut = () => {
    const el = containerRef.current;
    if (!el) return apply(Math.max(MIN_SCALE, scale / 1.5), tx, ty);
    const rect = el.getBoundingClientRect();
    setScaleAt(Math.max(MIN_SCALE, scale / 1.5), rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      pinchStart.current = {
        dist: Math.hypot(dx, dy) || 1,
        scale,
        midX: (pts[0].x + pts[1].x) / 2,
        midY: (pts[0].y + pts[1].y) / 2,
        tx,
        ty,
      };
      panStart.current = null;
    } else if (pointers.current.size === 1 && scale > 1) {
      panStart.current = { x: e.clientX, y: e.clientY, tx, ty };
      // Double-tap detect
      const now = Date.now();
      if (lastTap.current && now - lastTap.current.t < 300 && Math.hypot(e.clientX - lastTap.current.x, e.clientY - lastTap.current.y) < 30) {
        // toggle to 1×
        reset();
        lastTap.current = null;
      } else {
        lastTap.current = { t: now, x: e.clientX, y: e.clientY };
      }
    } else if (pointers.current.size === 1) {
      const now = Date.now();
      if (lastTap.current && now - lastTap.current.t < 300 && Math.hypot(e.clientX - lastTap.current.x, e.clientY - lastTap.current.y) < 30) {
        setScaleAt(2, e.clientX, e.clientY);
        lastTap.current = null;
      } else {
        lastTap.current = { t: now, x: e.clientX, y: e.clientY };
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const pts = Array.from(pointers.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const ps = pinchStart.current;
      const targetScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, ps.scale * (dist / ps.dist)));
      // Anchor on initial midpoint relative to container
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = ps.midX - rect.left;
      const py = ps.midY - rect.top;
      const ratio = targetScale / ps.scale;
      const nx = px - (px - ps.tx) * ratio;
      const ny = py - (py - ps.ty) * ratio;
      apply(targetScale, nx, ny);
    } else if (pointers.current.size === 1 && panStart.current) {
      const ps = panStart.current;
      apply(scale, ps.tx + (e.clientX - ps.x), ps.ty + (e.clientY - ps.y));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey) return; // trackpad pinch reports ctrlKey
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    setScaleAt(scale * factor, e.clientX, e.clientY);
  };

  const handlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onPointerLeave: onPointerUp,
    onWheel,
  };

  const transformStyle: React.CSSProperties = {
    transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
    transformOrigin: "0 0",
    willChange: "transform",
    transition: pointers.current.size > 0 ? "none" : "transform 120ms ease-out",
  };

  return { scale, transformStyle, handlers, zoomIn, zoomOut, reset, hintAt };
}
